import Grid from '@/components/atoms/legacy/Grid';
import Heading, { HeadingLevels } from '@/components/atoms/legacy/Heading';
import Wrapper from '@/components/atoms/legacy/Wrapper';
import Breadcrumb from '@/components/molecules/Breadcrumb';
import PageHeader from '@/components/organisms/PageHeader';
import scrollIntoView from '@/lib/scrollIntoView';
import React, { useMemo } from 'react';

const PrivacyPolicyTemplate: React.FC = () => {
  const p1El = React.useRef(null);
  const p2El = React.useRef(null);
  const p3El = React.useRef(null);
  const p4El = React.useRef(null);
  const p5El = React.useRef(null);
  const p6El = React.useRef(null);
  const p7El = React.useRef(null);
  const p8El = React.useRef(null);
  const p9El = React.useRef(null);

  const nav = useMemo(
    () => [
      {
        text: '1. General',
        el: p1El,
      },
      {
        text: '2. What personal information do we collect',
        el: p2El,
      },
      {
        text: '3. How we use your personal information',
        el: p3El,
      },
      {
        text: '4. Marketing Purposes',
        el: p4El,
      },
      {
        text: '5. Advertising',
        el: p5El,
      },
      {
        text: '6. Cookies',
        el: p6El,
      },
      {
        text: '7. Accessing, Reviewing and Changing Your Personal Information',
        el: p7El,
      },
      {
        text: '8. Protection and retention of your personal information',
        el: p8El,
      },
      {
        text: '9. Other Information',
        el: p9El,
      },
    ],
    []
  );

  return (
    <>
      <Wrapper className="mb-[8px]">
        <Breadcrumb
          links={[
            {
              text: 'Home',
              href: '/',
            },
            {
              text: 'Privacy Policy',
              href: '/privacy_policy',
            },
          ]}
        />
      </Wrapper>
      <Wrapper className="mb-[20px]">
        <PageHeader label="Privacy Policy" />
      </Wrapper>
      <Wrapper className="bg-white pt-[20px]">
        <Grid>
          <section className="col-span-full l:col-span-3">
            <nav className="mb-[60px] bg-blue-light p-[20px] l:sticky l:top-0">
              {nav.map(({ text, el }) => (
                <button
                  key={text}
                  className="w-full p-[10px] text-left text-bodyBold4 text-neutral-800"
                  onClick={() => {
                    if (el.current) {
                      scrollIntoView(el.current);
                    }
                  }}
                >
                  {text}
                </button>
              ))}
            </nav>
          </section>
          <article className="col-span-full l:col-span-8 l:col-start-5">
            <section ref={p1El} className="mb-[60px]">
              <Heading
                className="mb-[10px] text-h4Regular"
                level={HeadingLevels.H2}
              >
                1. General
              </Heading>
              <p className="text-microNormal text-neutral-800">
                This privacy policy outlines the approach of EV.com regarding
                the gathering, usage, storage, sharing, and safeguarding of your
                personal information (&quot;Privacy Policy&quot;). This Privacy
                Policy pertains to the website www.ev.com and all associated
                websites, applications, services, and tools where reference is
                made to this policy (&quot;Services&quot;), regardless of how
                you access the Services, including access through mobile
                devices. EV.com is the data controller.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Scope and Consent: By using EV.com and its related services, you
                provide explicit consent to EV.com for the collection, usage,
                disclosure, and retention of your personal information by us, as
                described in this Privacy Policy and our Terms of Use.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                EV.com may modify this Privacy Policy from time to time. We
                encourage you to review it frequently. Any significant changes
                to our Privacy Policy will be communicated on our website. The
                updated Privacy Policy will be effective immediately upon its
                initial posting on our website. This Privacy Policy has been
                effective since January 1, 2023.
              </p>
            </section>
            <section ref={p2El} className="mb-[60px]">
              <Heading
                className="mb-[10px] text-h4Regular"
                level={HeadingLevels.H2}
              >
                2. What personal information do we collect
              </Heading>
              <p className="text-microNormal text-neutral-800">
                You can browse our website without creating an account. However,
                when you choose to provide us with your personal information,
                you acknowledge that such information is transmitted to and
                stored on our servers. We collect the following categories of
                personal information:
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Automatically collected information: When you visit our website,
                utilize our services, or interact with ads or other content, we
                automatically gather information sent to us by your computer,
                mobile device, or any other equipment used to access our
                services. This information includes, but is not limited to:
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Data regarding your interaction with our website and services,
                such as device type, device ID, computer and connection
                information, geo-location data, statistics on page views,
                traffic to and from www.ev.com, referring URL, ad data, IP
                address, and standard web log data.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Data we obtain through the use of cookies, web beacons, and
                other similar technologies.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Information you provide to us: We collect and store any data you
                input on our website or submit to us when using our services.
                This data includes, but is not limited to:
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Information provided to us during registration for an account or
                for the services you use.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Additional data that you may provide to us through third-party
                services or social media sites.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Information shared with us during dispute resolution,
                correspondence through our website, or correspondence sent to
                us.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Data about your location and your device&apos;s location,
                including unique device identifiers, if enabled on your mobile
                device.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Your resume if submitted to us for consideration through our
                careers page.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Information from other sources: We may receive or gather
                additional information about you from third-party sources and
                add it to your account information. This information includes,
                but is not limited to, demographic data, navigation data,
                additional contact data, and additional data about you from
                public authorities, as permitted by law.
              </p>
            </section>
            <section ref={p3El} className="mb-[60px]">
              <Heading
                className="mb-[10px] text-h4Regular"
                level={HeadingLevels.H2}
              >
                3. How we use your personal information
              </Heading>
              <p className="text-microNormal text-neutral-800">
                By using EV.com, you acknowledge and consent to our use of your
                personal information for the following purposes: To grant you
                access to our Services and Customer Support through email or
                phone.
              </p>
              <br />
              <ul className="list-disc pl-[20px] text-microNormal text-neutral-800">
                <li>
                  To prevent, detect, and investigate potentially prohibited or
                  illegal activities, as well as fraud and security breaches,
                  and to enforce our Terms of Use.
                </li>
                <li>
                  To personalize, measure, and enhance our Services, content,
                  and advertisements.
                </li>
                <li>
                  To contact you via email, push notification, text message
                  (SMS), or phone to inquire about our Services and those of
                  affiliated companies within the EV.com &ldquo;corporate
                  family&ldquo; (visit www.ebayclassifiedsgroup.com for more
                  information) for targeted marketing activities, updates, and
                  promotional offers based on your communication preferences,
                  where applicable, or for any other purposes as described in
                  this Privacy Policy.
                </li>
                <li>
                  To provide you with any other requested services, as specified
                  when we collected your information.
                </li>
                <li>
                  To evaluate your potential candidacy if you decide to submit
                  your resume through our careers page.
                </li>
              </ul>
              <br />
              <p className="text-microNormal text-neutral-800">
                Sharing information with and registering on social media sites:
                We offer sign-on Services that enable you to access our Website
                or related sites using your login credentials. We also provide
                Services that allow you to share information with third-party
                social media sites like Facebook, Google Plus, and Twitter. If
                you grant us access to personal information stored on
                third-party websites, the level of access to this personal
                information will vary per website and will be determined by your
                own browser settings and your consent. If you decide to connect
                your third-party account to your EV.com account and grant us
                access to the information in those third-party accounts, you
                agree that we may collect, use, and store information from these
                third-party websites in accordance with this Privacy Policy.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Transfer of your personal information to third parties: In
                accordance with this Privacy Policy and applicable laws and
                regulations, EV.com may disclose your personal information to
                third parties. As stated in Section 4, your personal information
                will not be disclosed to third parties for their marketing
                purposes without your explicit consent. Your personal
                information may be shared with:
              </p>
              <br />
              <ul className="list-disc pl-[20px] text-microNormal text-neutral-800">
                <li>
                  Companies within the EV.com group to provide joint content and
                  services such as registration and customer support. This is
                  done to prevent, detect, and investigate potential illegal
                  activities, policy violations, fraud, and data security
                  breaches. In addition, this information is used to support
                  decision-making on products, websites, applications, services,
                  tools, and communication. If you have requested services from
                  one of the EV.com group companies, they may use your personal
                  information to send you marketing messages in accordance with
                  their privacy policy.
                </li>
                <li>
                  Service providers with whom EV.com has entered into an
                  agreement to assist in providing Services on the website, such
                  as financial service providers, marketing agencies, and
                  technical support. In these cases, your personal information
                  will remain under the control of EV.com. If you are listing an
                  ad, your listing or business address may be displayed on a map
                  in your ads (including within the EV.com mobile application)
                  via Google Maps and therefore transferred to Google (refer to
                  the Google Privacy Notice for more info).
                </li>
                <li>
                  Certain third parties, such as intellectual property rights
                  holders, supervisory authorities, tax authorities, police, and
                  other regulatory authorities if required by law or in
                  accordance with the Privacy Policy. EV.com may share your
                  personal information if necessary:
                </li>
                <ul className="list-disc pl-[20px]">
                  <li>To comply with legal obligations or a court order.</li>
                  <li>
                    To prevent, detect, or prosecute criminal offenses such as
                    fraud, deceit, or prosecution.
                  </li>
                  <li>
                    To maintain our policies or protect the rights and freedoms
                    of others.
                  </li>
                </ul>
                <li>
                  Other third parties to whom you have given consent to share
                  your information via EV.com, such as in a cooperation.
                </li>
                <li>
                  Companies that EV.com intends to merge with in the context of
                  a re-organization or that acquire EV.com.
                </li>
                <li>
                  An intellectual property owner if the owner of an intellectual
                  property right or an intermediary believes in good faith that
                  an advertisement violates the rights of the owner. Before
                  personal information is provided, the IP owner will enter into
                  an agreement that, among other things, stipulates that
                  information is only provided on the strict condition that it
                  may only be used in the context of legal proceedings and/or
                  obtaining legal advice and/or to answer questions from the
                  relevant advertiser.
                </li>
              </ul>
              <br />
              <p className="text-microNormal text-neutral-800">
                In our efforts to respect your privacy and keep the website free
                of malicious parties, EV.com will not disclose your personal
                information to third parties without a court order or formal
                request from the government in accordance with applicable law,
                unless we believe in good faith that such disclosure is
                necessary to prevent impending injury or financial damages or to
                report alleged illegal activities.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Information you share on www.EV.com: Our website enables users
                to share advertisements and other information with other users,
                making the shared information accessible to other users. Since
                our website also allows direct contact between buyers and
                sellers, we recommend that you consider how you share your
                personal information with others. You are solely responsible for
                the personal information you share through our website, and we
                cannot guarantee the privacy or security of the information you
                share with other users.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                If you visit our website from a shared computer or a computer in
                an internet café, we strongly recommend that you log off after
                each session. If you do not want the shared computer to remember
                you and/or your credentials, you will need to remove cookies
                and/or the history of your website visits.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                EV.com&apos;s data controller may transfer data to other members
                of our corporate family as described in this Privacy Policy, who
                may process and keep your personal information on servers in the
                European Union, the United States, and our data centers in other
                parts of the world.
              </p>
            </section>
            <section ref={p4El} className="mb-[60px]">
              <Heading
                className="mb-[10px] text-h4Regular"
                level={HeadingLevels.H2}
              >
                4. Marketing Purposes
              </Heading>
              <p className="text-microNormal text-neutral-800">
                By using EV.com - Electric Vehicle Marketplace , you consent to
                our use of the information we collect to send you offers or
                contact you by telephone regarding products or services offered
                by EV.com - Electric Vehicle Marketplace or its affiliated
                companies, whether personalized or not.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                We will not share, sell, or rent your personal information to
                third parties for their marketing purposes without your explicit
                consent. We may combine your information with data we obtain
                from other companies to improve and personalize our services and
                features.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                If you no longer wish to receive marketing communications from
                us, you can update your preferences in your EV.com - Electric
                Vehicle Marketplace account, or click on the
                &quot;unsubscribe&quot; link in any marketing communication you
                receive where applicable.
              </p>
            </section>
            <section ref={p5El} className="mb-[60px]">
              <Heading
                className="mb-[10px] text-h4Regular"
                level={HeadingLevels.H2}
              >
                5. Advertising
              </Heading>
              <p className="text-microNormal text-neutral-800">
                EV.com - Electric Vehicle Marketplace follows the principles of
                the Digital Advertising Alliance of Canada&apos;s (DAAC)
                AdChoices program. If you do not want to participate in our
                personalized advertising using your personal information or
                tracking/retargeting programs, you can opt-out by following the
                instructions provided in the applicable advertisement or
                directly on the AdChoices website provided by the DAAC, and
                using the opt-out mechanism. Opting out does not necessarily
                mean that you will not see any ads. It only means that such ads
                will not be personalized for you using cookies, web beacons, or
                similar technologies.
              </p>
            </section>
            <section ref={p6El} className="mb-[60px]">
              <Heading
                className="mb-[10px] text-h4Regular"
                level={HeadingLevels.H2}
              >
                6. Cookies
              </Heading>
              <p className="text-microNormal text-neutral-800">
                When using our Services, EV.com - Electric Vehicle Marketplace
                and our service providers may use cookies (data files placed on
                your phone or mobile device&apos;s drive) or web beacons
                (electronic images placed in a web page&apos;s code) or similar
                technologies. These technologies help us to identify you as a
                user, provide you with a better experience on our website,
                measure the effectiveness of our promotions, and ensure trust
                and safety on our website. To learn more about how we use these
                technologies, please refer to our Cookies, Web Beacons, and
                Similar Technologies Policy.
              </p>
            </section>
            <section ref={p7El} className="mb-[60px]">
              <Heading
                className="mb-[10px] text-h4Regular"
                level={HeadingLevels.H2}
              >
                7. Accessing, Reviewing and Changing Your Personal Information
              </Heading>
              <p className="text-microNormal text-neutral-800">
                We cannot modify your personal information or account
                information. However, you can modify your information yourself
                by logging into your EV.com - Electric Vehicle Marketplace
                account. Please note that once you&apos;ve placed a listing, you
                may not be able to change or delete your message. If you want to
                close your account (where applicable) with us, you can submit a
                request by clicking here. We&apos;ll process your request within
                a reasonable period of time and handle your personal information
                in accordance with applicable laws.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                If you need to access your personal information that is not
                directly accessible on EV.com - Electric Vehicle Marketplace ,
                you can contact our Customer Support. We may charge a fee to
                cover our costs, but the fee will not exceed the amount
                permitted by law. If we have any of your information that is
                factually incorrect, incomplete, or irrelevant, you can request
                that we modify or delete it. We will handle such requests in
                accordance with applicable laws.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                If you have any questions about your personal information,
                please contact us using the subject line &quot;Request Privacy
                Policy.&quot;
              </p>
            </section>
            <section ref={p8El} className="mb-[60px]">
              <Heading
                className="mb-[10px] text-h4Regular"
                level={HeadingLevels.H2}
              >
                8. Protection and retention of your personal information
              </Heading>
              <p className="text-microNormal text-neutral-800">
                At EV.com - Electric Vehicle Marketplace , we take measures to
                protect your personal information using technical and
                administrative security measures like firewalls, data
                encryption, physical and administrative access controls to data
                and servers to minimize the risk of loss, abuse, unauthorized
                access, disclosure, and alteration. If you suspect that your
                account has been misused, please reach out to us via the Contact
                Form.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                We retain personal information only as long as it is legally
                permissible and necessary for the purposes described above. When
                it is no longer required, we delete the personal information.
              </p>
            </section>
            <section ref={p9El} className="mb-[60px]">
              <Heading
                className="mb-[10px] text-h4Regular"
                level={HeadingLevels.H2}
              >
                9. Other Information
              </Heading>
              <p className="text-microNormal text-neutral-800">
                At EV.com - Electric Vehicle Marketplace , we have a
                zero-tolerance policy towards website abuse. You are not allowed
                to add other EV.com - Electric Vehicle Marketplace users to your
                mailing list for commercial purposes, even if they have made a
                purchase from you, without their explicit consent. If you come
                across any abuse of our website, such as spam or spoof emails,
                please inform us immediately through our contact form.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Using our communication resources to send spam or violate our
                terms of use is prohibited. For security reasons, we may scan
                messages automatically to check for spam, viruses, phishing, and
                other malicious or illegal content. We do not store messages
                sent through these resources permanently.
              </p>
              <br />
              <p className="text-microNormal text-neutral-800">
                Please note that this Privacy Policy only applies to the use and
                transfer of information that we collect from you. Third-party
                privacy policies that may apply to you are not within our
                control. When we work with third parties or use third-party
                tools to provide certain services, we will clearly indicate
                which privacy policy applies to you. We encourage you to ask
                questions before disclosing your personal information to others.
              </p>
            </section>
          </article>
        </Grid>
      </Wrapper>
    </>
  );
};

export default PrivacyPolicyTemplate;
